import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	canUserInvite,
	getConfluencePermittedUsers,
	getInviteesWithConfluenceAccess,
	getInviteesWithoutJiraAccess,
	getUsersFromCollaborationGraph,
} from '@atlassian/invite-prompt';
import type { Invitees, User } from '@atlassian/invite-prompt';

import { useSessionData } from '@confluence/session-data';

import { useFeatureExposed } from '../feature-exposure';

export const useGetInvitees = () => {
	const { cloudId: confluenceCloudId, userId } = useSessionData();
	const fireExposure = useFeatureExposed();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		async (spaceKey: string, jiraCloudId: string): Promise<Invitees> => {
			if (!userId) {
				return [];
			}

			const collaborators = await getUsersFromCollaborationGraph(confluenceCloudId, userId);

			if (!collaborators) {
				fireExposure('NO_COLLABORATORS_FOUND');
				return [];
			}

			const invitees = collaborators.filter(
				(c): c is User => c.email !== undefined && c.email !== null,
			);

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'filtered',
					actionSubject: 'collaborators',
					source: 'inviteOnLinkInsert',
					attributes: {
						collaborators: collaborators.length,
						invitees: invitees.length,
					},
				},
			}).fire();

			if (invitees.length === 0) {
				fireExposure('NO_COLLABORATORS_FOUND');
				return [];
			}

			const inviteesWithoutJiraAccess = await getInviteesWithoutJiraAccess(jiraCloudId, invitees);
			if (inviteesWithoutJiraAccess.length === 0) {
				fireExposure('COLLABORATORS_ALREADY_HAVE_ACCESS_TO_LINK');
				return [];
			}

			const inviteesWithConfluenceAccess = await getInviteesWithConfluenceAccess(
				confluenceCloudId,
				spaceKey,
				inviteesWithoutJiraAccess,
			);

			getConfluencePermittedUsers(
				confluenceCloudId,
				inviteesWithoutJiraAccess.map((c) => c.id),
			)
				.then((accountIds) => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'filtered',
							actionSubject: 'collaborators',
							source: 'inviteOnLinkInsert',
							attributes: {
								withSpaceAccess: inviteesWithConfluenceAccess.length,
								withConfluenceAccess: accountIds.length,
							},
						},
					}).fire();
				})
				.catch(() => {});

			if (inviteesWithConfluenceAccess.length === 0) {
				fireExposure('COLLABORATORS_HAVE_NO_ACCESS_TO_SPACE');
				return [];
			}

			if (!(await canUserInvite(jiraCloudId))) {
				fireExposure('NOT_ELIGIBLE_FOR_INVITE');
				return [];
			}

			return inviteesWithConfluenceAccess;
		},
		[confluenceCloudId, createAnalyticsEvent, fireExposure, userId],
	);
};
